import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Gamepad2, MoreVertical, Plus, Search } from 'lucide-react';
import type { DeviceDto, GameDto } from '@gc/shared';

import { api } from '@/lib/api';
import { DevicePathEditor, toDevicePathPayload, toOverrideMap } from './DevicePathEditor';
import { Field, MediaUpload, ghostPillClass, inputClass, pillClass } from './controls';
import { buildGamePayload, type MediaIdEdit } from './gameLogic';
import {
  launchTargetFieldProblems,
  mapDevicePathFieldErrors,
  overrideErrorMap,
  overrideField,
  suggestCompany,
} from './launchTargetPresenter';
import { toFieldErrors, useResource, type FieldError } from './useSettings';

/**
 * Games (SPEC §6.6) — a grid of cover tiles with a ⋮ menu per tile, search,
 * category filter and Add New Game.
 */
export function GamesPage() {
  const { t } = useTranslation();

  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [editing, setEditing] = useState<GameDto | null>(null);
  const [creating, setCreating] = useState(false);
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const query = debounced ? `?search=${encodeURIComponent(debounced)}` : '';
  const { data, loading, reload } = useResource<GameDto[]>(`/settings/games${query}`);
  const games = data ?? [];

  async function handleDelete(game: GameDto): Promise<void> {
    if (!window.confirm(t('settings.games.confirmDelete', { title: game.title }))) return;
    setMenuFor(null);
    try {
      await api.delete(`/settings/games/${game.id}`);
      await reload();
    } catch (caught) {
      setError(toFieldErrors(caught, t('settings.games.deleteFailed'))[0]?.message ?? null);
    }
  }

  return (
    <div className="p-6">
      <header className="mb-4 flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold text-content">{t('settings.nav.games')}</h1>

        <div className="relative">
          <Search
            className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-y-1/2 text-content-muted"
            style={{ insetInlineStart: '0.6rem' }}
            aria-hidden
          />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('settings.games.searchPlaceholder')}
            className={`${inputClass} w-56 ps-8`}
          />
        </div>

        <div className="flex-1" />

        <button type="button" onClick={() => setCreating(true)} className={pillClass}>
          <span className="inline-flex items-center gap-1">
            <Plus className="h-4 w-4" aria-hidden />
            {t('settings.games.addNew')}
          </span>
        </button>
      </header>

      {error ? (
        <p role="alert" className="mb-3 text-sm text-danger">
          {error}
        </p>
      ) : null}

      {loading && games.length === 0 ? (
        <p className="text-sm text-content-muted">{t('common.loading')}</p>
      ) : games.length === 0 ? (
        <p className="text-sm text-content-muted">{t('common.noResults')}</p>
      ) : (
        <div className="grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(9rem,1fr))]">
          {games.map((game) => (
            <article
              key={game.id}
              className="relative overflow-hidden rounded-card border border-subtle bg-raised"
            >
              <div className="grid aspect-[3/4] place-items-center bg-input">
                {game.coverUrl ? (
                  <img src={game.coverUrl} alt="" className="h-full w-full object-cover" />
                ) : (
                  <Gamepad2 className="h-8 w-8 text-content-muted" aria-hidden />
                )}
              </div>

              <div className="flex items-start gap-1 p-2">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm text-content">{game.title}</p>
                  <p className="truncate text-xs text-content-muted">
                    {game.company ?? '—'}
                    {game.devicePaths.length > 0
                      ? ` · ${t('settings.paths.overrideCount', { count: game.devicePaths.length })}`
                      : ''}
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => setMenuFor(menuFor === game.id ? null : game.id)}
                  title={t('common.actions')}
                  aria-label={t('common.actions')}
                  className="grid h-7 w-7 shrink-0 place-items-center rounded-full text-content-secondary hover:bg-hover"
                >
                  <MoreVertical className="h-4 w-4" aria-hidden />
                </button>
              </div>

              {menuFor === game.id ? (
                <div className="absolute end-2 bottom-10 z-10 w-32 overflow-hidden rounded-control border border-subtle bg-overlay shadow-xl">
                  <button
                    type="button"
                    onClick={() => {
                      setEditing(game);
                      setMenuFor(null);
                    }}
                    className="block w-full px-3 py-2 text-start text-sm text-content hover:bg-hover"
                  >
                    {t('common.edit')}
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleDelete(game)}
                    className="block w-full px-3 py-2 text-start text-sm text-danger hover:bg-hover"
                  >
                    {t('common.delete')}
                  </button>
                </div>
              ) : null}
            </article>
          ))}
        </div>
      )}

      {creating ? (
        <GameDrawer game={null} onClose={() => setCreating(false)} onSaved={() => void reload()} />
      ) : null}

      {editing ? (
        <GameDrawer game={editing} onClose={() => setEditing(null)} onSaved={() => void reload()} />
      ) : null}
    </div>
  );
}

/** Add/edit a game: title, cover, category, company, and the path model. */
function GameDrawer({
  game,
  onClose,
  onSaved,
}: {
  game: GameDto | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const isEdit = game !== null;

  const devices = useResource<{ devices: DeviceDto[] }>('/devices');
  // Until the list has loaded there is no way to tell a hidden device from
  // a slow one, so no override is treated as unlisted before then.
  const visibleDeviceIds = devices.data ? devices.data.devices.map((entry) => entry.id) : undefined;

  const [title, setTitle] = useState(game?.title ?? '');
  const [company, setCompany] = useState(game?.company ?? '');
  const [defaultPath, setDefaultPath] = useState(game?.executablePath ?? '');
  const [overrides, setOverrides] = useState<Record<string, string>>(
    toOverrideMap(game?.devicePaths ?? []),
  );
  // `undefined` = untouched, `null` = cleared. See `MediaIdEdit`'s docblock —
  // collapsing the two is what made the trash icon a no-op on save.
  const [coverMediaAssetId, setCoverMediaAssetId] = useState<MediaIdEdit>(undefined);
  const [coverUrl, setCoverUrl] = useState<string | null>(game?.coverUrl ?? null);
  const [trailerMediaAssetId, setTrailerMediaAssetId] = useState<MediaIdEdit>(undefined);
  const [trailerUrl, setTrailerUrl] = useState<string | null>(game?.trailerUrl ?? null);

  const [errors, setErrors] = useState<FieldError[]>([]);
  const [submitting, setSubmitting] = useState(false);
  // The launcher label this drawer filled into "Launcher / company" itself,
  // so a later link may replace it — while anything staff typed stays.
  const suggestedCompany = useRef<string | null>(null);

  const errorFor = (field: string) => errors.find((issue) => issue.field === field)?.message;
  const clearError = (field: string) =>
    setErrors((current) => current.filter((issue) => issue.field !== field));

  function handleDefaultPathChange(value: string): void {
    setDefaultPath(value);
    clearError('executablePath');
    const suggestion = suggestCompany(company, suggestedCompany.current, value);
    if (suggestion !== null) {
      suggestedCompany.current = suggestion;
      setCompany(suggestion);
    }
  }

  function handleOverridesChange(next: Record<string, string>): void {
    for (const deviceId of Object.keys(next)) {
      if (next[deviceId] !== overrides[deviceId]) clearError(overrideField(deviceId));
    }
    setOverrides(next);
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setErrors([]);

    // The same parser the API runs, run first: a path or link no PC could
    // open is refused under its own field without a round trip.
    const problems = launchTargetFieldProblems(defaultPath, overrides, visibleDeviceIds);
    if (problems.length > 0) {
      setErrors(problems.map(({ field, key, params }) => ({ field, message: t(key, params) })));
      return;
    }

    setSubmitting(true);

    const common = buildGamePayload({
      title,
      company,
      executablePath: defaultPath,
      devicePaths: toDevicePathPayload(overrides),
      coverMediaAssetId,
      trailerMediaAssetId,
    });

    try {
      if (isEdit && game) {
        await api.patch(`/settings/games/${game.id}`, common);
      } else {
        await api.post('/settings/games', common);
      }
      onSaved();
      onClose();
    } catch (caught) {
      // `devicePaths.<index>.path` names an entry of the array sent above;
      // file it under that entry's device so it shows beside the row.
      setErrors(
        mapDevicePathFieldErrors(
          toFieldErrors(caught, t('settings.games.saveFailed')),
          overrides,
          visibleDeviceIds,
        ),
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/60">
      <form
        onSubmit={(event) => void handleSubmit(event)}
        className="flex h-full w-full max-w-xl flex-col border-s border-subtle bg-raised shadow-2xl"
      >
        <header className="border-b border-subtle p-5">
          <h2 className="text-lg font-semibold text-content">
            {isEdit ? t('settings.games.edit') : t('settings.games.addNew')}
          </h2>
        </header>

        <div className="min-h-0 flex-1 space-y-3 overflow-auto p-5">
          <div className="flex flex-wrap items-start gap-6">
            <MediaUpload
              label={t('settings.games.cover')}
              kind="GAME_COVER"
              currentUrl={coverUrl}
              onUploaded={(assetId, url) => {
                setCoverMediaAssetId(assetId);
                setCoverUrl(url);
              }}
              onCleared={() => {
                setCoverMediaAssetId(null);
                setCoverUrl(null);
              }}
            />

            {/*
              The trailer column, the API schemas and the kiosk delivery all
              existed already — this control was the only missing piece. The
              hint says what the file becomes and what plays well there
              (owner, 2026-09-23): the kiosk loops it muted behind the game.
            */}
            <div className="min-w-56 flex-1">
              <MediaUpload
                label={t('settings.games.trailer')}
                kind="GAME_TRAILER"
                currentUrl={trailerUrl}
                onUploaded={(assetId, url) => {
                  setTrailerMediaAssetId(assetId);
                  setTrailerUrl(url);
                }}
                onCleared={() => {
                  setTrailerMediaAssetId(null);
                  setTrailerUrl(null);
                }}
              />
              <p className="mt-1 text-xs text-content-muted">{t('settings.games.trailerHint')}</p>
            </div>
          </div>

          <Field label={t('settings.games.gameTitle')} error={errorFor('title')}>
            <input
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              required
              className={inputClass}
            />
          </Field>

          <Field label={t('settings.games.launcher')} error={errorFor('company')}>
            <input
              value={company}
              onChange={(event) => setCompany(event.target.value)}
              placeholder="Steam"
              className={inputClass}
            />
          </Field>

          <DevicePathEditor
            devices={devices.data?.devices ?? []}
            defaultPath={defaultPath}
            onDefaultPathChange={handleDefaultPathChange}
            overrides={overrides}
            onOverridesChange={handleOverridesChange}
            detected={game?.devicePaths}
            defaultPathError={errorFor('executablePath')}
            overrideErrors={overrideErrorMap(errors)}
          />
        </div>

        <footer className="border-t border-subtle p-5">
          {errorFor('(root)') ? (
            <p role="alert" className="mb-3 text-sm text-danger">
              {errorFor('(root)')}
            </p>
          ) : null}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className={ghostPillClass}>
              {t('common.cancel')}
            </button>
            <button type="submit" disabled={submitting} className={pillClass}>
              {t('common.save')}
            </button>
          </div>
        </footer>
      </form>
    </div>
  );
}
